/**
 * OAuth2 Flow Hook
 * Manages OAuth2 authorization flow initiation for WiCloud
 *
 * Supports multiple OAuth2 providers via backend configuration.
 * Provider name is passed as a parameter (defaults to 'wiaccount').
 */

import { useState } from "react";
import {
  oauth2Service,
  OAUTH2_SESSION_KEY,
  OAuth2SessionData,
  isCryptoSupported,
} from "@/lib/oauth2";

export interface UseOAuth2FlowReturn {
  initiateOAuth2: (provider?: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for initiating OAuth2 authorization flow
 *
 * @example
 * ```tsx
 * function OAuthButton({ provider }) {
 *   const { initiateOAuth2, isLoading } = useOAuth2Flow();
 *
 *   return (
 *     <Button onClick={() => initiateOAuth2(provider)} disabled={isLoading}>
 *       {isLoading ? 'Redirecting...' : `Sign in with ${provider.displayName}`}
 *     </Button>
 *   );
 * }
 * ```
 */
export const useOAuth2Flow = (): UseOAuth2FlowReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initiateOAuth2 = async (provider = "wiaccount") => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Validate browser capabilities
      if (!isCryptoSupported()) {
        throw new Error(
          "Your browser does not support required security features for OAuth2",
        );
      }

      // 2. Prepare redirect URI (using consistent /auth/oauth2/callback route)
      const redirectUri = `${window.location.origin}/auth/oauth2/callback`;

      // 3. Call backend to initiate OAuth2 flow
      console.log(`[OAuth2Flow] Initiating flow for provider: ${provider}`);
      const response = await oauth2Service.initiate({
        provider,
        redirectUri,
      });

      // 4. Store OAuth2 session data securely in localStorage
      const sessionData: OAuth2SessionData = {
        state: response.state,
        codeVerifier: response.codeVerifier,
        timestamp: Date.now(),
        redirectUri,
        provider,
      };

      localStorage.setItem(OAUTH2_SESSION_KEY, JSON.stringify(sessionData));

      // 5. Redirect to authorization endpoint (prompt param already set by backend adapter)
      window.location.href = response.authorizationUrl;
    } catch (err) {
      console.error("[OAuth2Flow] Initiation failed:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to initiate OAuth2 flow";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const clearError = () => setError(null);

  return { initiateOAuth2, isLoading, error, clearError };
};
